import {
  Component,
  OnInit,
  ViewChild,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import {
  AuthService,
  DataService,
  ToasterService,
} from '../../../shared/services';
import { Router } from '@angular/router';
import { GridOptions } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ImpactModel } from '../../../shared/models';
import { ConfirmModalComponent } from '../../../shared/modals';
import { GridComponent } from '../../../shared';
import { ImpactEditComponent } from './impact-edit/edit.component';

import * as _ from 'lodash';

import { BreadCrumbService } from '../../../shared/services/breadcrumb.service';

@Component({
  selector: 'app-impacts',
  templateUrl: './impacts.component.html',
})
export class ImpactsListComponent implements OnInit, OnChanges {
  @ViewChild(GridComponent, { static: true }) impactGrid?: GridComponent;

  @Input() selectedFunctionLossNodes: any[] = [];
  @Output() resetModelsGrid = new EventEmitter<any>();
  // Available items
  items: any[] = [];
  selectedNodes: any[] = [];
  parentFiltered?: any;
  // UI flag
  isLoading = false;
  // Local grid options
  gridOptions?: GridOptions;
  // Modal ref pointer
  bsModalRef?: BsModalRef;
  isReferenceLoaded = false;
  productsReference?: any;
  configConsequencesReference?: any;
  impactGridAutoGroupColumnDef?: any;
  modelDescriptions: any = {};
  functionFailureDescriptions: any = {};
  productDescriptions: any = {};
  consequenceDescriptions: any = {};

  constructor(
    private translate: TranslateService,
    private data: DataService,
    private bsModalService: BsModalService,
    private toastr: ToasterService,
    public auth: AuthService,
    public router: Router,
    public breadCrumbService: BreadCrumbService
  ) {}

  /**
   */
  async ngOnInit(): Promise<void> {
    const tmpGridOptions: GridOptions = {
      getRowId: (row: any) => row.data.id,
      suppressRowClickSelection: false,
      groupSelectsChildren: true,
      rowSelection: 'multiple',
      rowHeight: 30,
      headerHeight: 30,
      animateRows: true,
      floatingFiltersHeight: 30,
      enableCellTextSelection: false,
      suppressContextMenu: true,
      groupDefaultExpanded: -1, // expand all groups by default
      defaultColDef: {
        filter: true,
        sortable: true,
        resizable: true,
        floatingFilter: true,
      },
      columnDefs: [
        {
          headerName: this.translate.instant(
            'headers.fastcr.risk_model.risk_model'
          ),
          field: 'model_name',
          valueGetter: (params: any) => {
            if (params.data) {
              return params.data.model_id;
            }
          },
          filterValueGetter: (params: any) => {
            if (params.data) {
              return params.data.model_name;
            }
          },
          cellRenderer: (params: any) => {
            return this.modelDescriptions?.[params.value];
          },
          rowGroup: true,
          hide: true,
        },
        {
          headerName: this.translate.instant(
            'headers.fastcr.function_loss.function_loss'
          ),
          field: 'function_failure_description',
          valueGetter: (params: any) => {
            if (params.data) {
              return params.data.function_failure_id;
            }
          },
          filterValueGetter: (params: any) => {
            if (params.data) {
              return params.data.function_failure_description;
            }
          },
          cellRenderer: (params: any) => {
            return this.functionFailureDescriptions?.[params.value];
          },
          rowGroup: true,
          hide: true,
        },
        {
          headerName: this.translate.instant('headers.fastcr.function_loss.id'),
          field: 'function_loss_id',
          hide: true,
        },
        {
          headerName: this.translate.instant('headers.fastcr.impact.consequence'),
          field: 'consequence_description',
          valueGetter: (params: any) => {
            if (params.data) {
              return params.data.consequence_id;
            }
          },
          filterValueGetter: (params: any) => {
            if (params.data) {
              return params.data.consequence_description;
            }
          },
          cellRenderer: (params: any) => {
            return this.consequenceDescriptions?.[params.value];
          },
          rowGroup: true,
          hide: true,
        },
        {
          headerName: this.translate.instant('headers.fastcr.impact.product'),
          field: 'product_description',
          valueGetter: (params: any) => {
            if (params.data) {
              return params.data.product_id;
            }
          },
          filterValueGetter: (params: any) => {
            if (params.data) {
              return params.data.product_description;
            }
          },
          cellRenderer: (params: any) => {
            if (params.node.group) {
              return '';
            }
            return this.productDescriptions?.[params.value]?.value;
          },
          hide: true,
        },
        {
          headerName: this.translate.instant('headers.fastcr.impact.probability'),
          field: 'probabilityConsequence',
          cellRenderer: (params: any): string => {
            if (params.node.group) {
              return '';
            }
            if (params.value !== undefined && params.value !== null) {
              return params.value + '%';
            }
            return 'N/A';
          },
          cellRendererParams: {
            suppressDoubleClickExpand: true,
          },
        },
      ],
    };
    this.impactGridAutoGroupColumnDef = {
      headerName: this.translate.instant('headers.fastcr.impact.consequence'),
      field: 'consequence_description',
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' },
      cellRendererParams: {
        checkbox: true,
        innerRenderer: (params: any) => {
          if (params.node.group) {
            // Level 0: Risk Model
            if (params.node.level === 0) {
              return this.modelDescriptions?.[params.value] || params.value;
            }
            // Level 1: Function Loss
            if (params.node.level === 1) {
              return this.functionFailureDescriptions?.[params.value] || params.value;
            }
            // Level 2: Consequence
            if (params.node.level === 2) {
              return this.consequenceDescriptions?.[params.value] || params.value;
            }
          }
          // For leaf rows, show product name
          if (!params.node.group && params.data) {
            return this.productDescriptions?.[params.data.product_id]?.value || '';
          }
          return params.value;
        },
        suppressDoubleClickExpand: true,
      },
      sortable: true,
      sort: 'asc',
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
    };

    await this.loadItems();
    await this.loadProducts();
    this.gridOptions = tmpGridOptions;
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.selectedFunctionLossNodes) {
      this.filterImpacts(this.selectedFunctionLossNodes);
    }
  }

  clearFilterAndResetModelsGrid(): void {
    this.impactGrid?.clearAllFilters();
    this.resetModelsGrid.emit();
  }

  filterImpacts(riskModels: any): void {
    const instance =
      this.impactGrid?.gridApi?.getFilterInstance('function_loss_id');

    if (riskModels.length > 0) {
      setTimeout(() => {
        instance?.setModel({
          values: _.map(riskModels, (el) => el.id + ''),
        });
        this.impactGrid?.gridApi?.onFilterChanged();
      });
      return;
    }

    instance?.setModel(null);
    this.impactGrid?.gridApi?.onFilterChanged();
  }

  /**
   */
  async loadItems(silent = false): Promise<void> {
    if (!silent) {
      this.isLoading = true;
    }
    const items = await this.data
      .by('api/fastcrit/impacts', {
        criteria: {
          site: this.auth.getSiteId(),
        },
      })
      .toPromise();

    // this.impactGrid?.gridApi?.sizeColumnsToFit();
    this.impactGrid?.uncheckAll();
    this.filterImpacts(this.selectedFunctionLossNodes);
    
    // set data array to use ID as value in valueGetter and Description as display value.
    // This is to allow grouping by ID instead of display value ("name" for eg.) thereby avoid grouping on duplicate display values
    // Create separate rows for each product (hierarchy: Risk Model > Function Loss > Consequence > Product)
    const records: any[] = [];
    
    items.forEach((item: any) => {
      this.modelDescriptions[item.fastcrFunctionLoss.fastcrModel.id] =
        item.fastcrFunctionLoss.fastcrModel.description;
      this.functionFailureDescriptions[
        item.fastcrFunctionLoss.configFunctionalFailure.id
      ] = item.fastcrFunctionLoss.configFunctionalFailure.description;
      this.productDescriptions[item.configProduct.id] = {
        id: item.configProduct.id,
        value: item.configProduct.description,
      };
      this.consequenceDescriptions[item.configConsequence.id] =
        item.configConsequence.description;

      // Create a row for each product
      records.push({
        probabilityConsequence: item.probabilityConsequence,
        model_name: item.fastcrFunctionLoss.fastcrModel.description,
        model_id: item.fastcrFunctionLoss.fastcrModel.id,
        function_failure_description:
          item.fastcrFunctionLoss.configFunctionalFailure.description,
        function_failure_id: item.fastcrFunctionLoss.configFunctionalFailure.id,
        consequence_description: item.configConsequence.description,
        consequence_id: item.configConsequence.id,
        product_description: item.configProduct.description,
        product_id: item.configProduct.id,
        function_loss_id: item.fastcrFunctionLoss.id,
        id: item.id,
      });
    });

    // assign formatted values
    this.items = records;

    if (!silent) {
      this.isLoading = false;
    }
  }

  onSelectionChanged(event: any): void {
    this.selectedNodes = event;
  }

  onRowDoubleClicked(event: any): void {
    if (!event || this.auth.isViewer('fast_criticality')) {
      return;
    }
    
    // Handle new format: object with { data, node } for group rows
    if (event.node && event.node.group) {
      // Check if it's a consequence level group (level 2)
      if (event.node.level === 2) {
        const consequenceId = event.node.key;
        // Get all leaf children data under this consequence node
        const leafChildrenData: any[] = [];
        event.node.allLeafChildren?.forEach((childNode: any) => {
          if (childNode.data) {
            leafChildrenData.push(childNode.data);
          }
        });
        
        if (leafChildrenData.length > 0) {
          // Find all items in this.items that match the leaf children
          // This ensures we get all products under this consequence
          const allRowsForConsequence = this.items.filter((item: any) => 
            leafChildrenData.some((leaf: any) => 
              item.id === leaf.id
            )
          );
          
          if (allRowsForConsequence.length > 0) {
            this.showEditModal(allRowsForConsequence);
          }
        }
      }
      return;
    }
    
    // Handle old format: data directly (for leaf rows and backward compatibility)
    const clickedRow = event.data || event;
    if (clickedRow && clickedRow.consequence_id) {
      const allRowsForConsequence = this.items.filter((item: any) => 
        item.consequence_id === clickedRow.consequence_id &&
        item.function_loss_id === clickedRow.function_loss_id
      );
      
      if (allRowsForConsequence.length > 0) {
        this.showEditModal(allRowsForConsequence);
      }
    }
  }

  showMassEditModal(): void {
    if (this.selectedNodes && this.selectedNodes.length > 0) {
      const items = _.map(this.selectedNodes, (el) => el.data);
      this.showEditModal(items);
    }
  }

  /**
   * @param items - Array of rows (one per product in new format)
   */
  async showEditModal(items?: any[]): Promise<void> {
    await this.loadConsequences();
    await this.loadProducts();
    
    // Transform from new format (one row per product) to old format (one row per consequence with product columns)
    // Group by consequence_id and function_loss_id
    const transformedItems: any[] = [];
    if (items && items.length > 0) {
      const grouped = _.groupBy(items, (item: any) => 
        `${item.consequence_id}_${item.function_loss_id}`
      );
      
      Object.values(grouped).forEach((group: any) => {
        const firstItem = group[0];
        const transformedItem: any = {
          id: firstItem.id,
          consequence_id: firstItem.consequence_id,
          function_loss_id: firstItem.function_loss_id,
          model_id: firstItem.model_id,
          function_failure_id: firstItem.function_failure_id,
        };
        
        // Add probability for each product that exists in the group
        group.forEach((row: any) => {
          transformedItem[`probabilityConsequence_${row.product_id}`] = 
            row.probabilityConsequence;
        });
        
        // Ensure all products are represented (even if null/undefined)
        // The edit component will show empty fields for products without values
        if (this.productsReference) {
          this.productsReference.forEach((product: any) => {
            const productKey = `probabilityConsequence_${product.value}`;
            if (!transformedItem.hasOwnProperty(productKey)) {
              transformedItem[productKey] = null;
            }
          });
        }
        
        transformedItems.push(transformedItem);
      });
    }
    
    this.bsModalRef = this.bsModalService.show(ImpactEditComponent, {
      initialState: {
        items: transformedItems,
        productsReference: this.productsReference,
        configConsequencesReference: this.configConsequencesReference,
        selectedFunctionLossNodes: this.selectedFunctionLossNodes,
      },
    });
    this.bsModalRef.content.confirmFn = () => {
      this.loadItems();
      this.toastr.showSuccess();
    };
  }

  // Load Products used in modal edit form
  async loadProducts(): Promise<void> {
    // Products
    const orderByDesc = { orderBy: { description: 'asc' } };
    const tpm = await this.data
      .by('api/config/products', {
        criteria: {
          account: this.auth.getAccountId(),
        },
        ...orderByDesc,
      })
      .toPromise();
    this.productsReference = _.map(tpm, (el: any) => {
      return {
        name: el.description,
        value: el.id,
      };
    });
  }

  // Load Consequences used in modal edit form
  async loadConsequences(): Promise<void> {
    // Consequences
    const tpm = await this.data
      .by('api/config/consequences', {
        criteria: {
          account: this.auth.getAccountId(),
        },
      })
      .toPromise();

    const data = _.orderBy(tpm, ['category_name', 'cost'], ['asc', 'asc']);
    this.configConsequencesReference = _.map(data, (el: any) => {
      return {
        name: el.description,
        value: el.id,
      };
    });

    // Mark ref as loaded (prevent reload)
    this.isReferenceLoaded = true;
  }

  /**
   */
  showMassDeleteConfirm(): void {
    this.bsModalRef = this.bsModalService.show(ConfirmModalComponent, {
      initialState: {
        title: 'navigations.delete',
        content: '',
      },
    });
    this.bsModalRef.content.confirmFn = () => {
      // Filter to only Product (leaf) nodes
      const leafNodes = this.selectedNodes.filter(
        (node: any) => !node.group && node.data
      );

      if (leafNodes.length === 0) {
        this.toastr.showError();
        this.bsModalRef?.hide();
        return;
      }

      // Group selected products by consequence_id and function_loss_id
      const grouped = _.groupBy(leafNodes, (node: any) =>
        `${node.data.consequence_id}_${node.data.function_loss_id}`
      );

      // Build array for API call - same structure as mass Edit API
      const deleteData = Object.values(grouped).map((group: any[]) => {
        const firstNode = group[0].data;
        return {
          configConsequence: firstNode.consequence_id,
          function_loss: firstNode.function_loss_id,
          products: group.map((node: any) => ({
            productId: node.data.product_id,
            probabilityConsequence: 0,
          })),
        };
      });

      this.data.saveAll('api/fastcrit/impacts', deleteData).subscribe(
        (res) => {
          this.loadItems();
          const message = res?.success
            ? 'messages.delete'
            : 'messages.delete_partial';
          this.toastr.showSuccess(message);
        },
        (err) => {
          this.toastr.showError();
        }
      );
      this.bsModalRef?.hide();
    };
    this.bsModalRef.content.cancelFn = () => {
      this.bsModalRef?.hide();
    };
  }

  myCellCallback = (params: any) => {
    return params.value;
  };

  exportProcessCallBack = (cellParams: any) => {
    if (cellParams.value && cellParams.column.colId === 'model_name') {
      return this.modelDescriptions?.[cellParams.value];
    }
    if (
      cellParams.value &&
      cellParams.column.colId === 'function_failure_description'
    ) {
      return this.functionFailureDescriptions?.[cellParams.value];
    }
    if (cellParams.value && cellParams.column.colId === 'consequence_description') {
      return this.consequenceDescriptions?.[cellParams.value];
    }
    if (cellParams.value && cellParams.column.colId === 'product_description') {
      return this.productDescriptions?.[cellParams.value]?.value;
    }
    if (cellParams.value && cellParams.node.field === 'model_name') {
      return this.modelDescriptions?.[cellParams.node.key];
    }

    if (
      cellParams.value &&
      cellParams.node.field === 'function_failure_description'
    ) {
      return this.functionFailureDescriptions?.[cellParams.node.key];
    }

    if (cellParams.value && cellParams.node.field === 'consequence_description') {
      return this.consequenceDescriptions?.[cellParams.node.key];
    }

    if (cellParams.value && cellParams.node.field === 'product_description') {
      return this.productDescriptions?.[cellParams.node.key]?.value;
    }

    if (cellParams.node.group) {
      return '';
    }

    if (cellParams.column.colId === 'probabilityConsequence') {
      if (cellParams.value !== undefined && cellParams.value !== null) {
        return cellParams.value + '%';
      }
      return 'N/A';
    }

    return cellParams.value;
  };
}
